import { useConfirmingLeave } from "@lhd/app/index";
import Button from "@lhd/components/Button";
import {
  createFieldCode,
  generateUuid,
  isEmployeeInfoChildAppPartType,
} from "@lhd/features/Apps/utils";
import { parseJson, toComponentName } from "@lhd/utility";
import { Form, Modal, Space } from "antd";
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FormActionContext } from "../..";
import {
  APP_PART_TYPES,
  EMPLOYEE_INFO_CHILDREN_SELECTION,
  JP_APP_PART_TYPES,
  UNIT_OF_MEASURE_TYPE,
} from "../../constants";
import { AppPartType, FormActionContextState } from "../../types";
import * as SettingComponents from "./SettingComponents";
import { useSubmitFormState } from "@lhd/components/FormState/hook";
import { cloneDeep, isArray } from "lodash";

type PropsType = {
  appPart?: AppPartType;
  setEditingAppPart: (appPart?: AppPartType) => void;
  isInList?: boolean;
  onSaveSettings?: (appPart: AppPartType) => void;
  isAllowUpdateApp?: boolean;
};

const isEmployeeInfoChildOf = (item: AppPartType, parentI?: string) => {
  if (!parentI) return false;
  const config =
    typeof item.item_config_data === "string"
      ? parseJson(item.item_config_data, {})
      : item.item_config_data;
  return config?.employee_info_parent_i === parentI;
};

const buildEmployeeInfoChildPart = ({
  tag,
  parent,
  layoutForFieldCode,
  itemConfigData,
}: {
  tag: typeof EMPLOYEE_INFO_CHILDREN_SELECTION[number];
  parent: AppPartType;
  layoutForFieldCode: AppPartType[];
  itemConfigData: AppPartType["item_config_data"];
}): AppPartType =>
  ({
    ...cloneDeep(tag),
    uuid: generateUuid(),
    id: undefined,
    field_code: createFieldCode(tag.field_code || tag.name, layoutForFieldCode),
    item_config_data: {
      ...cloneDeep(tag.item_config_data),
      ...itemConfigData,
      employee_info_parent_i: parent.item_config_data.i,
      employee_info_color:
        itemConfigData.employee_info_color ??
        parent.item_config_data.employee_info_color,
    },
  } as AppPartType);

const syncEmployeeInfoChildrenToLayout = (
  draft: FormActionContextState,
  parent: AppPartType,
) => {
  const parentI = parent.item_config_data.i;
  const tableParentI = parent.item_config_data.parentI;
  const parentColor = parent.item_config_data.employee_info_color || "#174a83";
  const selectedSet = new Set(
    parent.item_config_data.employee_info_children || [],
  );
  const orderedTypes = EMPLOYEE_INFO_CHILDREN_SELECTION.map(
    (item) => item.app_part_type,
  ).filter((type) => selectedSet.has(type));

  const getTag = (type: string) =>
    EMPLOYEE_INFO_CHILDREN_SELECTION.find(
      (item) => item.app_part_type === type,
    );

  const syncColorToChildren = (children: AppPartType[]) => {
    children.forEach((item) => {
      if (!isEmployeeInfoChildOf(item, parentI)) return;
      item.item_config_data = {
        ...item.item_config_data,
        employee_info_color: parentColor,
      };
    });
  };

  if (tableParentI) {
    const table = draft.layout.find(
      (item) => item.item_config_data.i === tableParentI,
    );
    if (!table) return;
    table.children = (table.children || []).filter(
      (item) =>
        !isEmployeeInfoChildOf(item, parentI) ||
        selectedSet.has(item.app_part_type),
    );
    syncColorToChildren(table.children || []);
    const existingTypes = new Set(
      (table.children || [])
        .filter((item) => isEmployeeInfoChildOf(item, parentI))
        .map((item) => item.app_part_type),
    );
    orderedTypes.forEach((type) => {
      if (existingTypes.has(type)) return;
      const tag = getTag(type);
      if (!tag) return;
      const lineIndex = table.children?.length || 0;
      table.children = table.children || [];
      table.children.push(
        buildEmployeeInfoChildPart({
          tag,
          parent,
          layoutForFieldCode: [...draft.layout, ...table.children],
          itemConfigData: {
            ...cloneDeep(tag.item_config_data),
            parentI: tableParentI,
            i: `${tableParentI}-${lineIndex + 1}`,
            childIndex: lineIndex,
            employee_info_parent_i: parentI,
            employee_info_color: parentColor,
          } as AppPartType["item_config_data"],
        }),
      );
    });
    return;
  }

  draft.layout = draft.layout.filter(
    (item) =>
      !isEmployeeInfoChildOf(item, parentI) ||
      selectedSet.has(item.app_part_type),
  );
  syncColorToChildren(draft.layout);
  const existingTypes = new Set(
    draft.layout
      .filter((item) => isEmployeeInfoChildOf(item, parentI))
      .map((item) => item.app_part_type),
  );
  let nextY = parent.item_config_data.y + (parent.item_config_data.h || 0);
  draft.layout
    .filter((item) => isEmployeeInfoChildOf(item, parentI))
    .forEach((item) => {
      nextY = Math.max(
        nextY,
        item.item_config_data.y + (item.item_config_data.h || 0),
      );
    });
  orderedTypes.forEach((type) => {
    if (existingTypes.has(type)) return;
    const tag = getTag(type);
    if (!tag) return;
    const h = tag.item_config_data.h || 3;
    draft.layout.push(
      buildEmployeeInfoChildPart({
        tag,
        parent,
        layoutForFieldCode: draft.layout,
        itemConfigData: {
          ...cloneDeep(tag.item_config_data),
          i: generateUuid(),
          x: parent.item_config_data.x,
          y: nextY,
          w: parent.item_config_data.w,
          employee_info_parent_i: parentI,
          employee_info_color: parentColor,
        } as AppPartType["item_config_data"],
      }),
    );
    nextY += h;
  });
};

const COMMON_FIELDS = ["name", "field_code"] as const;
const REQUIRED_FIELDS_MAP: Record<string, readonly string[]> = {
  [APP_PART_TYPES.TEXT]: COMMON_FIELDS,
  [APP_PART_TYPES.TEXT_AREA]: COMMON_FIELDS,
  [APP_PART_TYPES.NUMBER]: COMMON_FIELDS,
  [APP_PART_TYPES.ATTACHMENT]: COMMON_FIELDS,
  [APP_PART_TYPES.DATE]: COMMON_FIELDS,
  [APP_PART_TYPES.TIME]: COMMON_FIELDS,
  [APP_PART_TYPES.DATE_AND_TIME]: COMMON_FIELDS,
  [APP_PART_TYPES.CREATED_BY]: COMMON_FIELDS,
  [APP_PART_TYPES.UPDATED_BY]: COMMON_FIELDS,
  [APP_PART_TYPES.CREATED_DATETIME]: COMMON_FIELDS,
  [APP_PART_TYPES.UPDATED_DATETIME]: COMMON_FIELDS,
  [APP_PART_TYPES.LINK]: COMMON_FIELDS,
  [APP_PART_TYPES.DROPDOWN]: COMMON_FIELDS,
  [APP_PART_TYPES.RICH_TEXT]: COMMON_FIELDS,
  [APP_PART_TYPES.RADIO_BUTTON]: COMMON_FIELDS,
  [APP_PART_TYPES.MULTI_CHOICE]: COMMON_FIELDS,
  [APP_PART_TYPES.RECORD_NUMBER]: COMMON_FIELDS,
  [APP_PART_TYPES.PRIMARY_COMPANY_NAME]: COMMON_FIELDS,
  [APP_PART_TYPES.TABLE]: COMMON_FIELDS,
  [APP_PART_TYPES.FIELD_GROUP]: COMMON_FIELDS,
  [APP_PART_TYPES.LOOKUP]: ["name"],
  [APP_PART_TYPES.ORGANIZATION_SELECTION]: COMMON_FIELDS,
  [APP_PART_TYPES.DIVISION_SELECTION]: COMMON_FIELDS,
  [APP_PART_TYPES.DEPARTMENT_SELECTION]: COMMON_FIELDS,
  [APP_PART_TYPES.BRANCH_SELECTION]: COMMON_FIELDS,
  [APP_PART_TYPES.WORKER_SELECTION]: COMMON_FIELDS,
  [APP_PART_TYPES.COMPANY_LOCATION_SELECTION]: COMMON_FIELDS,
  [APP_PART_TYPES.REPRESENTATIVE_SELECTION]: COMMON_FIELDS,
  [APP_PART_TYPES.COMPANY_PHONE_NUMBER_SELECTION]: COMMON_FIELDS,
  [APP_PART_TYPES.POSITION_SELECTION]: COMMON_FIELDS,
  [APP_PART_TYPES.EMPLOYEE_INFO_SELECTION]: COMMON_FIELDS,
  [APP_PART_TYPES.CUSTOMER_SELECTION]: COMMON_FIELDS,
  [APP_PART_TYPES.EMPLOYEE_SIGNATURE]: [
    ...COMMON_FIELDS,
    "referenced_field_code",
  ],
  [APP_PART_TYPES.CHECK_BOX]: COMMON_FIELDS,
  [APP_PART_TYPES.CALCULATED]: COMMON_FIELDS,
  [APP_PART_TYPES.RELATED_RECORDS]: [
    ...COMMON_FIELDS,
    "referenced_type",
    "referenced_app_part_id",
  ],
};

const ModalSetting: FC<PropsType> = ({
  appPart,
  setEditingAppPart,
  isInList,
  onSaveSettings,
  isAllowUpdateApp = true,
}) => {
  const appPartType = appPart?.app_part_type;
  const settingName = `${toComponentName(
    appPartType,
  )}Setting` as keyof typeof SettingComponents;
  const { checkLeaveForm } = useConfirmingLeave();
  const SettingComponent: any = isEmployeeInfoChildAppPartType(appPartType)
    ? SettingComponents.EmployeeInfoChildSelectionSetting
    : SettingComponents[settingName] || SettingComponents.GeneralSetting;

  const { contextState, setContextState, onHandleEditItem } =
    useContext(FormActionContext);

  const title = useMemo(() => {
    let result = "Setting";
    if (
      appPart?.app_part_type &&
      JP_APP_PART_TYPES[appPart.app_part_type as keyof typeof JP_APP_PART_TYPES]
    ) {
      result =
        JP_APP_PART_TYPES[
          appPart.app_part_type as keyof typeof JP_APP_PART_TYPES
        ];
    } else {
      result = `${appPart?.app_part_type
        .split("_")
        .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
        .join(" ")} Setting`;
    }
    return result;
  }, [appPart]);

  const isOpenModal = !!appPart;
  const [form] = Form.useForm();
  const options = Form.useWatch(["item_config_data", "options"], form);
  const [hasChanged, setHasChanged] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [blurFields, setBlurFields] = useState<Record<number, boolean>>({});
  const isEditModal = location.pathname.includes("app-grid");

  // Build dynamic required fields for options
  const requiredFields = useMemo(() => {
    const baseFields: Array<string | string[]> = [];

    if (!appPartType || !(appPartType in REQUIRED_FIELDS_MAP)) {
      return baseFields;
    }

    // Add common required fields
    baseFields.push(...REQUIRED_FIELDS_MAP[appPartType]);

    // Add dynamic options text fields for types that have options
    const typesWithOptions = [
      APP_PART_TYPES.DROPDOWN,
      APP_PART_TYPES.RADIO_BUTTON,
      APP_PART_TYPES.MULTI_CHOICE,
      APP_PART_TYPES.CHECK_BOX,
    ];

    if (
      appPartType &&
      typesWithOptions.includes(appPartType as any) &&
      Array.isArray(options)
    ) {
      options.forEach((_, index) => {
        baseFields.push(["item_config_data", "options", String(index), "text"]);
      });
    }

    return baseFields;
  }, [appPartType, options]);
  const {
    getButtonStyle,
    markSubmit,
    getFieldClassName,
    checkState,
    unMarkSubmit,
    buttonState,
  } = useSubmitFormState(form, false, requiredFields);

  const hasError = buttonState === "error";
  const onCloseModal = useCallback(() => {
    setContextState((draft) => {
      draft.isDisableDragging = false;
    });
    setEditingAppPart(undefined);
    if (isInList) {
      document.getElementsByClassName("app-part-type")[0]?.remove();
    }
    form.resetFields();
    unMarkSubmit();
    setHasChanged(false);
    setIsClicked(false);
    setBlurFields({});
  }, [setContextState, setEditingAppPart, isInList, form, unMarkSubmit]);

  const onFinish = useCallback(
    async (values) => {
      checkLeaveForm();
      const item_config_data =
        typeof appPart?.item_config_data === "string"
          ? parseJson(appPart?.item_config_data, {})
          : appPart?.item_config_data;
      const editItem = {
        ...appPart,
        ...values,
        item_config_data: {
          ...item_config_data,
          ...values.item_config_data,
        },
      };

      if (
        [APP_PART_TYPES.RADIO_BUTTON, APP_PART_TYPES.CHECK_BOX].includes(
          editItem.app_part_type,
        )
      ) {
        if (
          editItem?.item_config_data?.layout === "vertical" &&
          editItem?.item_config_data?.options?.length > 0 &&
          editItem.item_config_data.h <
            2 + editItem?.item_config_data?.options?.length
        ) {
          editItem.item_config_data.h =
            2 + editItem?.item_config_data?.options?.length;
        } else if (
          editItem?.item_config_data?.layout === "horizontal" &&
          editItem?.item_config_data?.options?.length > 0
        ) {
          editItem.item_config_data.h = 3;
        }
      }

      // because BE define unit_of_measure_type is string => quick fix to adapt requirement
      if (
        editItem?.app_part_type === APP_PART_TYPES.NUMBER &&
        isArray(editItem?.unit_of_measure_type)
      ) {
        if (editItem?.unit_of_measure_type?.length === 2) {
          editItem.unit_of_measure_type = UNIT_OF_MEASURE_TYPE.frefix_suffix;
        } else if (editItem?.unit_of_measure_type?.length === 1) {
          editItem.unit_of_measure_type = editItem?.unit_of_measure_type?.[0];
        } else {
          editItem.unit_of_measure_type = null;
        }
      }

      onHandleEditItem({
        editItem,
        preItemConfig: item_config_data,
      });
      if (editItem.app_part_type === APP_PART_TYPES.EMPLOYEE_INFO_SELECTION) {
        setContextState((draft) => {
          syncEmployeeInfoChildrenToLayout(draft, editItem);
        });
      }
      onSaveSettings && onSaveSettings(editItem);
      onCloseModal();
    },
    [
      checkLeaveForm,
      appPart,
      onHandleEditItem,
      onCloseModal,
      onSaveSettings,
      setContextState,
    ],
  );

  const initialValues = useMemo(() => {
    if (appPart) {
      const itemConfig =
        typeof appPart.item_config_data === "string"
          ? parseJson(appPart.item_config_data, {})
          : appPart.item_config_data || {};

      let employeeInfoChildren = itemConfig.employee_info_children;
      if (appPart.app_part_type === APP_PART_TYPES.EMPLOYEE_INFO_SELECTION) {
        const parentI = itemConfig.i;
        const currentChildrenTypes: string[] = [];
        const tableParentI = itemConfig.parentI;
        if (tableParentI) {
          const table = contextState.layout.find(
            (item) => item.item_config_data?.i === tableParentI,
          );
          (table?.children || []).forEach((child) => {
            if (isEmployeeInfoChildOf(child, parentI)) {
              currentChildrenTypes.push(child.app_part_type);
            }
          });
        } else {
          contextState.layout.forEach((item) => {
            if (isEmployeeInfoChildOf(item, parentI)) {
              currentChildrenTypes.push(item.app_part_type);
            }
          });
        }
        employeeInfoChildren = currentChildrenTypes;
      }

      let referencedFieldCode: string = appPart.referenced_field_code || "";
      if (
        !referencedFieldCode &&
        appPart.app_part_type === APP_PART_TYPES.EMPLOYEE_SIGNATURE
      ) {
        for (const p of contextState.layout || []) {
          if (
            p.app_part_type === APP_PART_TYPES.WORKER_SELECTION &&
            p.field_code &&
            p.item_config_data?.i !== itemConfig?.i
          ) {
            referencedFieldCode = p.field_code;
            break;
          }
          if (p.children && Array.isArray(p.children)) {
            const foundChild = p.children.find(
              (c) =>
                c.app_part_type === APP_PART_TYPES.WORKER_SELECTION &&
                c.field_code &&
                c.item_config_data?.i !== itemConfig?.i,
            );
            if (foundChild) {
              referencedFieldCode = foundChild?.field_code || "";
              break;
            }
          }
        }
      }

      return {
        ...appPart,
        referenced_field_code: referencedFieldCode,
        referenced_type:
          appPart.referenced_type || appPart.referenced_app_part?.app_id || "",
        item_config_data: {
          ...itemConfig,
          employee_info_children: employeeInfoChildren,
          employee_info_color: itemConfig.employee_info_color || "#174a83",
          link_type: "url",
          options: itemConfig.options || [{}],
          layout: itemConfig.layout || "horizontal",
        },
      };
    }
  }, [appPart, contextState.layout]);

  useEffect(() => {
    if (appPart && initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [appPart, initialValues, form]);

  useEffect(() => {
    checkState();
  }, [appPart, options, form]);

  const handleFieldChange = (value: { [key: string]: any }) => {
    if (value.referenced_app_part_id) {
      form.setFieldValue("default_value", undefined);
    }
    if (
      value.referenced_type &&
      appPart?.app_part_type === APP_PART_TYPES.EMPLOYEE_INFO_SELECTION
    ) {
      // Clear dependent fields + stale errors (getFieldsError stays wrong otherwise)
      form.setFields([
        { name: "referenced_app_part_id", value: undefined, errors: [] },
        { name: "referenced_worker_property", value: undefined, errors: [] },
        { name: "default_value", value: undefined, errors: [] },
      ]);
    }
    if (value.referenced_worker_property) {
      form.setFieldValue("default_value", undefined);
    }

    if (value.name) {
      form.setFieldValue(
        "field_code",
        createFieldCode(value.name, contextState.layout || []),
      );
    }
    checkState();
    setHasChanged(true);
  };

  if (!SettingComponent) return null;
  return (
    <Modal
      width={800}
      title={title}
      open={isOpenModal}
      onCancel={onCloseModal}
      maskClosable={false}
      footer={
        <Space direction="horizontal">
          <Button
            disabled={!isAllowUpdateApp || !hasChanged}
            missingRequire={hasError}
            label="はい"
            onClick={() => {
              form.submit();
              markSubmit();
              setIsClicked(true);
            }}
            style={
              hasError
                ? { ...getButtonStyle() }
                : !hasChanged && isEditModal
                ? { background: "#bcbcbc" }
                : {}
            }
          />
          <Button
            type="cancel-btn"
            danger
            label="いいえ"
            onClick={onCloseModal}
          />
        </Space>
      }
    >
      {appPart && (
        <Form
          form={form}
          initialValues={initialValues}
          onFinish={onFinish}
          layout="vertical"
          disabled={!isAllowUpdateApp}
          onValuesChange={handleFieldChange}
          validateTrigger="onBlur"
        >
          <SettingComponent
            form={form}
            appPart={appPart}
            getFieldClassName={getFieldClassName}
            isClicked={isClicked}
            blurFields={blurFields}
            setBlurFields={setBlurFields}
          />
        </Form>
      )}
    </Modal>
  );
};

export default ModalSetting;
